import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { getErrorHttpMessage } from "utils";
import {
  enviarAutorizarEquipoHttp,
  getCotizacionPorIdHttp,
  marcarSeleccionadosHttp,
  recotizarProductosHttp,
} from "actions/cotizacion";
import { createOrdenCompraHttp } from "actions/ordenCompra";
import { getProveedoresHttp } from "actions/proveedores";
import { useSeleccionProductos } from "modules/cotizacionesProveedor";
import { MENSAJES } from "../constants/gestionCotizacion.constants";
import type {
  Cotizacion,
  OpcionesGestionCotizacion,
  ProveedorCotizacion,
  Recotizacion,
} from "../types/gestionCotizacion.types";
import {
  actualizarRecotizacion,
  construirDatosBase,
  construirDatosOrden,
  recotizacionAPayload,
} from "../utils/gestionCotizacion.utils";
import { useAccionesCotizacion } from "./useAccionesCotizacion";

export const useGestionCotizacion = ({
  idUsuario,
  notificar,
  onCargando,
  proveedores: proveedoresExternos,
}: OpcionesGestionCotizacion) => {
  const intl = useIntl();
  const { seleccion, alternar, limpiar, idsDetalle } = useSeleccionProductos();

  const [cotizacion, setCotizacion] = useState<Cotizacion | null>(null);
  const [descripcion, setDescripcion] = useState("");
  const [recotizacion, setRecotizacion] = useState<Recotizacion>({});
  const [procesando, setProcesando] = useState(false);
  const [proveedoresPropios, setProveedoresPropios] = useState<any[]>([]);

  // Productos por proveedor
  const [cotizacionProductos, setCotizacionProductos] = useState<any>(null);
  const [isOpenProductosProveedor, setIsOpenProductosProveedor] = useState(false);
  const abrirProductosProveedor = () => setIsOpenProductosProveedor(true);
  const cerrarProductosProveedor = () => setIsOpenProductosProveedor(false);

  const proveedores = proveedoresExternos?.length ? proveedoresExternos : proveedoresPropios;

  const notificarRef = useRef(notificar);
  const onCargandoRef = useRef(onCargando);
  notificarRef.current = notificar;
  onCargandoRef.current = onCargando;

  const cargarCotizacion = useCallback(async (idCotizacion: number | string) => {
    try {
      onCargandoRef.current?.(true);
      const data = await getCotizacionPorIdHttp({ id_cotizacion: idCotizacion });
      setCotizacion(data as Cotizacion);
    } catch (error) {
      notificarRef.current(
        getErrorHttpMessage(error) || intl.formatMessage({ id: "get_elementos_error" })
      );
    } finally {
      onCargandoRef.current?.(false);
    }
  }, []);

  const recargar = useCallback(async () => {
    if (cotizacion?.id) await cargarCotizacion(cotizacion.id);
  }, [cotizacion?.id, cargarCotizacion]);

  // limpia al cerrar el modal
  const reiniciar = useCallback(() => {
    setCotizacion(null);
    setDescripcion("");
    setRecotizacion({});
    setCotizacionProductos(null);
    setIsOpenProductosProveedor(false);
    limpiar();
  }, [limpiar]);

  const datosBase = () => construirDatosBase(cotizacion, idUsuario, descripcion);

  /* ---------- Acciones ---------- */

  const marcarSeleccionados = async () => {
    if (idsDetalle.length === 0) return;
    try {
      setProcesando(true);
      await marcarSeleccionadosHttp({ ...datosBase(), ids_detalle: idsDetalle });
      limpiar();
      await recargar();
    } catch (error) {
      notificar(getErrorHttpMessage(error) || MENSAJES.marcarError);
    } finally {
      setProcesando(false);
    }
  };

  const crearOrdenesCompra = useCallback(
    async (lista: ProveedorCotizacion[]) => {
      let creadas = 0;
      let mensaje = intl.formatMessage({ id: "orden_compra_añadida_correctamente" });
      try {
        setProcesando(true);
        for (const proveedor of lista) {
          await createOrdenCompraHttp(construirDatosOrden(proveedor, datosBase()));
          creadas++;
        }
      } catch (error) {
        mensaje =
          getErrorHttpMessage(error) || intl.formatMessage({ id: "error_añadir_orden_compra" });
      } finally {
        if (creadas > 0) {
          limpiar();
          await recargar();
        }
        notificar(mensaje);
        setProcesando(false);
      }
    },
    [cotizacion, descripcion, idUsuario, recargar, limpiar, notificar, intl]
  );

  const enviarAutorizarEquipo = useCallback(
    async (idsMarcados: number[]) => {
      let mensaje = intl.formatMessage({ id: "enviado_autorizar_correctamente" });
      try {
        setProcesando(true);
        await enviarAutorizarEquipoHttp({ ...datosBase(), ids_detalle: idsMarcados });
        limpiar();
        await recargar();
      } catch (error) {
        mensaje =
          getErrorHttpMessage(error) || intl.formatMessage({ id: "enviado_autorizar_error" });
      } finally {
        notificar(mensaje);
        setProcesando(false);
      }
    },
    [cotizacion, descripcion, idUsuario, recargar, limpiar, notificar, intl]
  );

  const cambiarRecotizacion = (idInventario: number, ids: number[]) =>
    setRecotizacion((prev) => actualizarRecotizacion(prev, idInventario, ids));

  const recotizarProductos = useCallback(async () => {
    const productos = recotizacionAPayload(recotizacion);
    if (productos.length === 0 || !cotizacion) return;
    let mensaje: string = MENSAJES.recotizarOk;
    try {
      setProcesando(true);
      await recotizarProductosHttp({
        id_cotizacion: cotizacion.id,
        id_usuario: idUsuario,
        productos,
      });
      setRecotizacion({});
      await recargar();
    } catch (error) {
      mensaje = getErrorHttpMessage(error) || MENSAJES.recotizarError;
    } finally {
      notificar(mensaje);
      setProcesando(false);
    }
  }, [recotizacion, cotizacion, idUsuario, recargar, notificar]);

  const derivados = useAccionesCotizacion({
    cotizacion,
    procesando,
    idsDetalle,
    recotizacion,
    onEnviarAutorizar: enviarAutorizarEquipo,
    onRecotizar: recotizarProductos,
    onCrearOrdenes: crearOrdenesCompra,
  });

  // Carga proveedores solo si hay rechazados por el proveedor
  useEffect(() => {
    if (!derivados.hayRecotizables || proveedores.length > 0) return;
    getProveedoresHttp()
      .then((lista: any) => setProveedoresPropios(lista))
      .catch((error) =>
        notificarRef.current(
          getErrorHttpMessage(error) || intl.formatMessage({ id: "get_elementos_error" })
        )
      );
  }, [derivados.hayRecotizables]);

  return {
    cotizacion,
    setCotizacion,
    proveedores,
    procesando,
    cargarCotizacion,
    recargar,
    reiniciar,
    seleccion,
    alternar,
    idsDetalle,
    marcarSeleccionados,
    descripcion,
    setDescripcion,
    recotizacion,
    cambiarRecotizacion,
    recotizarProductos,
    crearOrdenesCompra,
    enviarAutorizarEquipo,
    cotizacionProductos,
    setCotizacionProductos,
    isOpenProductosProveedor,
    abrirProductosProveedor,
    cerrarProductosProveedor,
    ...derivados,
  };
};

export type GestionCotizacion = ReturnType<typeof useGestionCotizacion>;
